import { CardBrand } from '../enum/card-brand'
import { Deletable } from '../interface/deletable'
import { Factory } from '../interface/factory'
import { Identifiable } from '../interface/identifiable'
import { Modifiable } from '../interface/modifiable'
import {
  applyEntitySchema,
  objectSchema,
  openUnion,
  optionalTolerant,
  parseEntity,
  sourceJson,
} from '../../_utils/fcdc-v2'
import * as z from 'zod'

export enum PaymentServiceProvider {
  Adyen = 'Adyen',
  Worldline = 'Worldline',
}

export enum PaymentMethodType {
  Card = 'Card',
  Swish = 'Swish',
  ApplePay = 'ApplePay',
  GooglePay = 'GooglePay',
  Vipps = 'Vipps',
}

const dateValue = optionalTolerant(z.union([z.string(), z.number()]))

const paymentMethodFields = {
  type: z.string(),
  id: optionalTolerant(z.string()),
  userId: optionalTolerant(z.string()),
  deleted: optionalTolerant(z.boolean()),
  created: dateValue,
  modified: dateValue,
}

const paymentMethodSchema = openUnion('type', {
  [PaymentMethodType.Card]: objectSchema({
    ...paymentMethodFields,
    type: z.literal(PaymentMethodType.Card),
    brand: optionalTolerant(z.string()),
    psp: optionalTolerant(z.string()),
    note: optionalTolerant(z.string()),
    truncatedCardNumber: optionalTolerant(z.string()),
    expiryMonth: optionalTolerant(z.number()),
    expiryYear: optionalTolerant(z.number()),
    token: optionalTolerant(z.string()),
  }),
})

const supportedPaymentMethodSchema = openUnion('type', {
  [PaymentMethodType.Card]: objectSchema({
    type: z.literal(PaymentMethodType.Card),
    providers: optionalTolerant(z.array(z.unknown())),
  }),
  [PaymentMethodType.Swish]: objectSchema({
    type: z.literal(PaymentMethodType.Swish),
    providers: optionalTolerant(z.array(z.unknown())),
  }),
  [PaymentMethodType.ApplePay]: objectSchema({
    type: z.literal(PaymentMethodType.ApplePay),
    providers: optionalTolerant(z.array(z.unknown())),
  }),
  [PaymentMethodType.GooglePay]: objectSchema({
    type: z.literal(PaymentMethodType.GooglePay),
    providers: optionalTolerant(z.array(z.unknown())),
  }),
  [PaymentMethodType.Vipps]: objectSchema({
    type: z.literal(PaymentMethodType.Vipps),
    providers: optionalTolerant(z.array(z.unknown())),
  }),
})

const providerSchema = objectSchema({
  name: optionalTolerant(z.string()),
  merchantAccount: optionalTolerant(z.string()),
  clientKey: optionalTolerant(z.string()),
})

export class PaymentMethod implements Identifiable, Modifiable, Deletable {
  type: PaymentMethodType
  id: string
  userId: string

  deleted?: boolean
  created: Date
  modified: Date
  constructor(json: any) {
    const source = sourceJson(json) as any
    this.type = source.type
    this.id = source.id
    this.userId = source.userId
    this.created = new Date(source.created)
    this.modified = new Date(source.modified)
    this.deleted = source.deleted ? Boolean(source.deleted) : false
    applyEntitySchema(this, paymentMethodSchema, source, {})
  }

  public static getFactory(): Factory<PaymentMethod> {
    return new (class implements Factory<PaymentMethod> {
      make(json: any): PaymentMethod {
        switch (json?.type) {
          case PaymentMethodType.Card:
            return new CardPaymentMethod(json)
          default:
            return parseEntity(paymentMethodSchema, json ?? {}) as unknown as PaymentMethod
        }
      }

      getTableName(): string {
        return 'payment_methods'
      }
    })()
  }
}

export class CardPaymentMethod extends PaymentMethod {
  brand: CardBrand
  psp: PaymentServiceProvider

  note?: string

  truncatedCardNumber: string
  expiryMonth: number
  expiryYear: number

  token: string

  constructor(json: any) {
    const source = sourceJson(json) as any
    super(source)
    this.brand = source.brand as CardBrand
    this.psp = this.normalizeProvider(source.psp)
    this.truncatedCardNumber = source.truncatedCardNumber
    this.expiryMonth = source.expiryMonth
    this.expiryYear = source.expiryYear
    this.token = source.token
    this.note = source.note ? source.note : undefined
  }

  private normalizeProvider(provider: unknown): PaymentServiceProvider {
    if (provider === PaymentServiceProvider.Worldline) {
      return PaymentServiceProvider.Worldline
    }
    if (provider === PaymentServiceProvider.Adyen) {
      return PaymentServiceProvider.Adyen
    }
    if (typeof provider === 'string' && provider.length > 0) {
      return provider as PaymentServiceProvider
    }
    return PaymentServiceProvider.Adyen
  }
}

export class SupportedPaymentMethod {
  type: PaymentMethodType
  providers: AgnosticAdyenServiceProvider[]

  constructor(json: any) {
    const source = sourceJson(json) as any
    const providers = Array.isArray(source.providers)
      ? source.providers.map((provider: unknown) => sourceJson(provider))
      : undefined
    const plain: Record<string, unknown> = { ...source }
    if (Array.isArray(providers)) {
      plain['providers'] = providers
    }
    this.type = source.type
    this.providers = Array.isArray(source.providers) ? source.providers : []
    applyEntitySchema(this, supportedPaymentMethodSchema, plain, { providers: [] })
  }

  public static getFactory(): Factory<SupportedPaymentMethod> {
    return new (class implements Factory<SupportedPaymentMethod> {
      make(json: any): SupportedPaymentMethod {
        switch (json?.type) {
          case PaymentMethodType.Card:
            return new SupportedPaymentMethodCard(json)
          case PaymentMethodType.Swish:
            return new SupportedPaymentMethodSwish(json)
          case PaymentMethodType.ApplePay:
            return new SupportedPaymentMethodApplePay(json)
          case PaymentMethodType.GooglePay:
            return new SupportedPaymentMethodGooglePay(json)
          case PaymentMethodType.Vipps:
            return new SupportedPaymentMethodVipps(json)
          default:
            return parseEntity(supportedPaymentMethodSchema, json ?? {}) as SupportedPaymentMethod
        }
      }

      getTableName(): string {
        throw new Error('SupportedPaymentMethod does not have a table.')
      }
    })()
  }
}

export class SupportedPaymentMethodCard extends SupportedPaymentMethod {
  constructor(json: any) {
    super(json)
  }
}
export class SupportedPaymentMethodSwish extends SupportedPaymentMethod {
  constructor(json: any) {
    super(json)
  }
}
export class SupportedPaymentMethodApplePay extends SupportedPaymentMethod {
  constructor(json: any) {
    super(json)
  }
}
export class SupportedPaymentMethodGooglePay extends SupportedPaymentMethod {
  constructor(json: any) {
    super(json)
  }
}
export class SupportedPaymentMethodVipps extends SupportedPaymentMethod {
  constructor(json: any) {
    super(json)
  }
}

export class AgnosticAdyenServiceProvider {
  name: string = 'Adyen'
  merchantAccount: string
  clientKey: string
  constructor(json: any) {
    const source = sourceJson(json) as any
    this.merchantAccount = source.merchantAccount
    this.clientKey = source.clientKey
    if (source?.name) {
      this.name = source.name
    }
    applyEntitySchema(this, providerSchema, source, {})
  }
}
